import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, CalendarX, Repeat, Mail, Quote, FileText, Settings as SettingsIcon, Download, LogOut, Loader2, Trash2, Plus, Star, AlertTriangle } from "lucide-react";
import Seo from "@/components/common/Seo";
import { Logo } from "@/components/layout/Logo";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIME_SLOTS } from "@/data/site";

const TABS = [
  { id: "bookings", label: "Bookings", icon: LayoutGrid },
  { id: "availability", label: "Availability", icon: CalendarX },
  { id: "subscriptions", label: "Subscriptions", icon: Repeat },
  { id: "marketing", label: "Marketing", icon: Mail },
  { id: "testimonials", label: "Testimonials", icon: Quote },
  { id: "blog", label: "Blog", icon: FileText },
  { id: "featured", label: "Featured", icon: Star },
  { id: "complaints", label: "Complaints", icon: AlertTriangle },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];
const STATUSES = ["new", "scheduled", "done"];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-brand-offwhite">
      <Seo title="Admin | Reverence Autos" description="Owner dashboard" path="/admin" />
      <header className="sticky top-0 z-40 border-b border-brand-silver/40 bg-white">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4"><Logo /><span className="rounded-full bg-brand-charcoal px-3 py-1 text-xs font-semibold text-white">Owner</span></div>
          <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-red"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-8">
        {stats && (
          <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-6">
            {[["Bookings", stats.bookings], ["New", stats.new_bookings], ["Subscriptions", stats.subscriptions], ["Subscribers", stats.subscribers], ["Customers", stats.customers], ["Open issues", stats.open_complaints ?? 0]].map(([l, v]) => (
              <div key={l} className={`rounded-xl border bg-white p-5 ${l === "Open issues" && v > 0 ? "border-brand-red/50" : "border-brand-silver/40"}`}>
                <p className={`font-serif text-3xl font-bold ${l === "Open issues" && v > 0 ? "text-brand-red" : "text-brand-charcoal"}`}>{v}</p>
                <p className="text-xs uppercase tracking-wider text-brand-muted">{l}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-brand-red text-white" : "border border-brand-silver/40 bg-white text-brand-muted hover:text-brand-charcoal"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "bookings" && <Bookings />}
        {tab === "availability" && <Availability />}
        {tab === "subscriptions" && <Subscriptions />}
        {tab === "marketing" && <Marketing />}
        {tab === "testimonials" && <Testimonials />}
        {tab === "blog" && <BlogAdmin />}
        {tab === "featured" && <FeaturedTab />}
        {tab === "complaints" && <Complaints />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function Panel({ children }) { return <div className="rounded-2xl border border-brand-silver/40 bg-white p-6">{children}</div>; }
function Loading() { return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-red" /></div>; }

function Bookings() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/bookings").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const update = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(`Marked ${status}`); load();
  };
  if (!rows) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-4 font-serif text-2xl font-bold text-brand-charcoal">Bookings inbox</h2>
      {rows.length === 0 && <p className="text-brand-muted">No bookings yet.</p>}
      <div className="space-y-3">
        {rows.map((b) => (
          <div key={b.id} data-testid={`admin-booking-${b.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-brand-silver/30 p-4">
            <div>
              <p className="font-semibold text-brand-charcoal">{b.name} · <span className="capitalize">{b.service || "—"}</span> · {(b.vehicles || []).join(", ")}</p>
              <p className="text-sm text-brand-muted">{b.date} · {b.time_slot} · {b.postal_code} · ${b.pricing?.total?.toFixed(2)}</p>
              <p className="text-xs text-brand-muted">{b.email} · {b.phone} · {b.address}</p>
              {b.notes && <p className="mt-1 text-xs italic text-brand-muted">"{b.notes}"</p>}
            </div>
            <div className="flex gap-1">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`booking-status-${b.id}-${s}`} onClick={() => update(b.id, s)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${b.status === s ? "bg-brand-red text-white" : "border border-brand-silver/40 text-brand-muted hover:text-brand-charcoal"}`}>{s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Availability() {
  const [blocks, setBlocks] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const load = useCallback(() => api.get("/admin/blocks").then(({ data }) => setBlocks(data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!date) return;
    await api.post("/admin/blocks", { date, slots });
    toast.success("Date blocked from the hero calendar"); setDate(""); setSlots([]); load();
  };
  const remove = async (d) => { await api.delete(`/admin/blocks/${d}`); load(); };
  if (!blocks) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-1 font-serif text-2xl font-bold text-brand-charcoal">Availability manager</h2>
      <p className="mb-5 text-sm text-brand-muted">Block dates or specific windows so they don't appear on the hero calendar. Leave windows empty to block the whole day.</p>
      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-brand-silver/30 bg-brand-offwhite p-4">
        <div><label className="mb-1 block text-xs uppercase text-brand-muted">Date</label>
          <input type="date" data-testid="block-date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" /></div>
        <div className="flex flex-wrap gap-2">
          {TIME_SLOTS.map((t) => (
            <button key={t.id} onClick={() => setSlots((s) => s.includes(t.id) ? s.filter((x) => x !== t.id) : [...s, t.id])}
              className={`rounded-full px-3 py-2 text-xs ${slots.includes(t.id) ? "bg-brand-red text-white" : "border border-brand-silver/40 text-brand-muted"}`}>{t.label}</button>
          ))}
        </div>
        <button data-testid="add-block" onClick={add} className="btn-red !py-2"><Plus className="h-4 w-4" /> Block</button>
      </div>
      <div className="space-y-2">
        {blocks.length === 0 && <p className="text-brand-muted">No blocked dates.</p>}
        {blocks.map((b) => (
          <div key={b.date} className="flex items-center justify-between rounded-lg border border-brand-silver/30 px-4 py-2">
            <span className="text-sm text-brand-charcoal">{b.date} — {(b.slots || []).length ? b.slots.join(", ") : "whole day"}</span>
            <button data-testid={`remove-block-${b.date}`} onClick={() => remove(b.date)} className="text-brand-muted hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Subscriptions() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/subscriptions").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-4 font-serif text-2xl font-bold text-brand-charcoal">Active subscriptions</h2>
      {rows.length === 0 && <p className="text-brand-muted">No subscriptions yet.</p>}
      <div className="space-y-3">
        {rows.map((b) => (
          <div key={b.id} className="flex items-center justify-between rounded-xl border border-brand-silver/30 p-4">
            <div><p className="font-semibold capitalize text-brand-charcoal">{b.plan} · {b.name}</p><p className="text-sm text-brand-muted">{b.email} · started {b.date}</p></div>
            <span className="font-serif text-lg font-bold text-brand-charcoal">${b.pricing?.total?.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Marketing() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/subscribers").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  return (
    <Panel>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-2xl font-bold text-brand-charcoal">Marketing list ({rows.length})</h2>
        <a href={`${API}/admin/subscribers/export`} data-testid="export-csv" className="btn-outline-dark !py-2"><Download className="h-4 w-4" /> Export CSV</a>
      </div>
      <div className="max-h-[500px] overflow-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-brand-silver/40 text-left text-brand-muted"><th className="py-2">Email</th><th>Source</th><th>Date</th></tr></thead>
          <tbody>
            {rows.map((s) => (<tr key={s.email} className="border-b border-brand-silver/20"><td className="py-2 text-brand-charcoal">{s.email}</td><td className="text-brand-muted">{s.source}</td><td className="text-brand-muted">{s.created_at?.slice(0, 10)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Testimonials() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", area: "", rating: 5, quote: "", active: true, order: 0 });
  const load = useCallback(() => api.get("/admin/testimonials").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.name || !form.quote) return;
    await api.post("/admin/testimonials", form); toast.success("Testimonial added"); setForm({ name: "", area: "", rating: 5, quote: "", active: true, order: 0 }); load();
  };
  const remove = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };
  if (!rows) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-4 font-serif text-2xl font-bold text-brand-charcoal">Testimonials</h2>
      <div className="mb-6 grid gap-3 rounded-xl border border-brand-silver/30 bg-brand-offwhite p-4 sm:grid-cols-2">
        <input data-testid="testi-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        <input data-testid="testi-area" placeholder="Neighborhood" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        <textarea data-testid="testi-quote" placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm sm:col-span-2" />
        <button data-testid="add-testimonial" onClick={add} className="btn-red !py-2 sm:col-span-2"><Plus className="h-4 w-4" /> Add testimonial</button>
      </div>
      <div className="space-y-2">
        {rows.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-lg border border-brand-silver/30 px-4 py-3">
            <div><p className="font-semibold text-brand-charcoal">{t.name} · <span className="text-brand-muted">{t.area}</span></p><p className="text-sm text-brand-muted">"{t.quote.slice(0, 70)}…"</p></div>
            <button data-testid={`remove-testi-${t.id}`} onClick={() => remove(t.id)} className="text-brand-muted hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function BlogAdmin() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", content: "", tags: "", image: "", read_min: 5 });
  const load = useCallback(() => api.get("/admin/blog").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.slug || !form.title || !form.content) { toast.error("Slug, title and content required"); return; }
    try {
      await api.post("/admin/blog", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean), faq: [] });
      toast.success("Post created"); setForm({ slug: "", title: "", excerpt: "", content: "", tags: "", image: "", read_min: 5 }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/admin/blog/${id}`); load(); };
  if (!rows) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-4 font-serif text-2xl font-bold text-brand-charcoal">Blog posts ({rows.length})</h2>
      <div className="mb-6 grid gap-3 rounded-xl border border-brand-silver/30 bg-brand-offwhite p-4 sm:grid-cols-2">
        <input data-testid="blog-slug" placeholder="slug-like-this" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        <input data-testid="blog-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        <input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        <textarea placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm sm:col-span-2" />
        <textarea data-testid="blog-content" placeholder="HTML content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className="rounded-lg border border-brand-silver/50 px-3 py-2 text-sm sm:col-span-2" />
        <button data-testid="add-blog" onClick={add} className="btn-red !py-2 sm:col-span-2"><Plus className="h-4 w-4" /> Publish post</button>
      </div>
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-lg border border-brand-silver/30 px-4 py-3">
            <div><p className="font-semibold text-brand-charcoal">{p.title}</p><p className="text-xs text-brand-muted">/{p.slug}</p></div>
            <button data-testid={`remove-blog-${p.id}`} onClick={() => remove(p.id)} className="text-brand-muted hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function FeaturedTab() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/featured").then(({ data }) => setData(data)), []);
  useEffect(() => { load(); }, [load]);
  const select = async (id) => {
    await api.put("/admin/featured", { id });
    toast.success("Featured car updated — it's now live on the home page.");
    load();
  };
  if (!data) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-1 font-serif text-2xl font-bold text-brand-charcoal">Featured Transformation</h2>
      <p className="mb-5 text-sm text-brand-muted">Pick which car is spotlighted in the big showcase near the top of the home page. Changes go live instantly.</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.options.map((o) => {
          const active = o.id === data.selected_id;
          return (
            <button
              key={o.id}
              data-testid={`featured-option-${o.id}`}
              onClick={() => select(o.id)}
              className={`group overflow-hidden rounded-xl border text-left transition-all ${active ? "border-brand-red ring-2 ring-brand-red" : "border-brand-silver/40 hover:border-brand-charcoal hover:-translate-y-0.5"}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={o.image} alt={o.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {active && <span className="absolute right-2 top-2 rounded-full bg-brand-red px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-white">Live</span>}
              </div>
              <p className="p-3 text-sm font-semibold text-brand-charcoal">{o.title}</p>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

function Complaints() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/complaints").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/complaints/${id}`, { status });
    toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
    load();
  };
  if (!rows) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-1 font-serif text-2xl font-bold text-brand-charcoal">Complaints</h2>
      <p className="mb-5 text-sm text-brand-muted">Issues raised by customers about a detail. You also get a text the moment one comes in.</p>
      {rows.length === 0 && <p className="text-brand-muted">No complaints — nice work.</p>}
      <div className="space-y-3">
        {rows.map((c) => (
          <div key={c.id} data-testid={`admin-complaint-${c.id}`}
            className={`rounded-xl border p-4 ${c.status === "open" ? "border-brand-red/40 bg-brand-red/5" : "border-brand-silver/30"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-brand-charcoal">{c.name} · <span className="font-normal text-brand-muted">{c.email}</span></p>
                <p className="text-xs text-brand-muted">{c.booking_summary || "No booking linked"} · {c.created_at?.slice(0, 10)}</p>
                <p className="mt-2 text-sm italic text-brand-charcoal">"{c.message}"</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${c.status === "open" ? "bg-brand-red text-white" : "bg-green-100 text-green-700"}`}>{c.status}</span>
                <button data-testid={`complaint-toggle-${c.id}`} onClick={() => setStatus(c.id, c.status === "open" ? "resolved" : "open")}
                  className="rounded-full border border-brand-silver/40 px-3 py-1 text-xs font-medium text-brand-muted hover:text-brand-charcoal">
                  {c.status === "open" ? "Mark resolved" : "Reopen"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    await api.put("/admin/settings", { owner_email: s.owner_email, notify_enabled: s.notify_enabled, summer_promo_enabled: s.summer_promo_enabled, social_proof: s.social_proof });
    toast.success("Settings saved");
  };
  if (!s) return <Loading />;
  return (
    <Panel>
      <h2 className="mb-4 font-serif text-2xl font-bold text-brand-charcoal">Settings</h2>
      <div className="grid gap-5 max-w-lg">
        <div>
          <label className="mb-1 block text-xs uppercase text-brand-muted">Owner notification email</label>
          <input data-testid="settings-owner-email" value={s.owner_email} onChange={(e) => setS({ ...s, owner_email: e.target.value })} className="w-full rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="settings-notify" checked={s.notify_enabled} onChange={(e) => setS({ ...s, notify_enabled: e.target.checked })} /> Send me a notification email for every new booking</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="settings-summer" checked={s.summer_promo_enabled} onChange={(e) => setS({ ...s, summer_promo_enabled: e.target.checked })} /> Summer Special 10% promo active</label>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="mb-1 block text-xs uppercase text-brand-muted">Detailers</label>
            <input type="number" value={s.social_proof?.detailers || 0} onChange={(e) => setS({ ...s, social_proof: { ...s.social_proof, detailers: +e.target.value } })} className="w-full rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" /></div>
          <div><label className="mb-1 block text-xs uppercase text-brand-muted">Areas served</label>
            <input type="number" value={s.social_proof?.areas || 0} onChange={(e) => setS({ ...s, social_proof: { ...s.social_proof, areas: +e.target.value } })} className="w-full rounded-lg border border-brand-silver/50 px-3 py-2 text-sm" /></div>
        </div>
        <button data-testid="save-settings" onClick={save} className="btn-red">Save settings</button>
      </div>
    </Panel>
  );
}
